// Display formatting for the Orders section (KOVA-797) — a port of the mobile app's
// `features/orders/model/format.ts` plus `openableUrl` from `banner-copy.ts`. en-US throughout. Not
// `cancelFlow/format.ts`'s `formatDate`: the designs date orders differently ("Jun 5", "Wed 12 Aug",
// "15-18 Jul"), never "Nov 30, 2026".
//
// Which way round a date reads follows its meaning: a date something happened on is month-first ("Jun 5",
// "Jun 23, 10:29 AM"), a date something is expected by is day-first ("Wed 12 Aug", "15-18 Jul").

const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const dayNumeric = new Intl.DateTimeFormat('en-US', { day: 'numeric' })
const monthShort = new Intl.DateTimeFormat('en-US', { month: 'short' })
const time12h = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
const calendarWeekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' })
const calendarDay = new Intl.DateTimeFormat('en-US', { day: 'numeric', timeZone: 'UTC' })
const calendarMonth = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' })
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/** `YYYY-MM-DD` as local midnight — the day the backend wrote, in any timezone. */
function parseDateOnly(value: string): Date | null {
	const match = DATE_ONLY.exec(value)
	if (!match) return null
	const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
	return Number.isNaN(date.getTime()) ? null : date
}

/** `YYYY-MM-DD` as UTC midnight, only if it names a real day ("2026-02-30" and "2025-02-29" don't). */
function parseCalendarDate(value: string): Date | null {
	const match = DATE_ONLY.exec(value)
	if (!match) return null
	const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
	const date = new Date(Date.UTC(year, month - 1, day))
	return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null
}

function parseTimestamp(value: string): Date | null {
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? null : date
}

/** "+1 815 632 7473" for a US number however it is spaced; anything else as the backend sent it. */
export function formatPhoneNumber(phone: string): string {
	const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(phone.replace(/[\s()-]/g, ''))
	return match ? `+1 ${match[1]} ${match[2]} ${match[3]}` : phone
}

/**
 * "Wed 12 Aug" — the next shipment's `estimatedShipAt`, a calendar day with no time: read in UTC so no
 * timezone moves it a day. Anything but a real `YYYY-MM-DD` → `null` ("Not scheduled").
 */
export function formatCalendarWeekday(value: string): string | null {
	const date = parseCalendarDate(value)
	return date ? `${calendarWeekday.format(date)} ${calendarDay.format(date)} ${calendarMonth.format(date)}` : null
}

/** "Jun 5" — the past list's badge suffix; a timestamp in the browser's timezone, or a `YYYY-MM-DD` day. */
export function formatCompletedDate(value: string): string | null {
	const date = parseDateOnly(value) ?? parseTimestamp(value)
	return date ? monthDay.format(date) : null
}

/**
 * A same-day or one-sided window collapses to a single date, and callers word around it differently, so the
 * kind travels with the text.
 */
export interface ArrivalRange {
	kind: 'single' | 'range'
	text: string
}

/** "15-18 Jul", "28 Jun - 2 Jul", or "15 Jul" — the details' "Est. delivery" from `arrivalFrom` / `arrivalTo`. */
export function formatArrivalRange(from: string | null, to: string | null): ArrivalRange | null {
	const fromDate = from ? parseDateOnly(from) : null
	const toDate = to ? parseDateOnly(to) : null
	if (fromDate && toDate && fromDate.getTime() !== toDate.getTime()) {
		if (monthShort.format(fromDate) === monthShort.format(toDate)) {
			return {
				kind: 'range',
				text: `${dayNumeric.format(fromDate)}-${dayNumeric.format(toDate)} ${monthShort.format(toDate)}`,
			}
		}
		return {
			kind: 'range',
			text: `${dayNumeric.format(fromDate)} ${monthShort.format(fromDate)} - ${dayNumeric.format(toDate)} ${monthShort.format(toDate)}`,
		}
	}
	const single = fromDate ?? toDate
	return single ? { kind: 'single', text: `${dayNumeric.format(single)} ${monthShort.format(single)}` } : null
}

/**
 * "Jun 23, 10:29 AM" — a timeline event in the browser's timezone. Always 12-hour, as Figma draws it (the
 * mobile app follows the device's clock setting, which a browser doesn't expose).
 */
export function formatEventTime(iso: string): string | null {
	const date = parseTimestamp(iso)
	return date ? `${monthDay.format(date)}, ${time12h.format(date)}` : null
}

export function formatCardMask(last4: string): string {
	return `**** **** **** ${last4}`
}

/**
 * The URL to open, or `null` when it is not one we follow. Only `https://` (any case) after trimming; the
 * result is trimmed with the scheme lowercased, so no caller validates one form and opens another. Only the
 * scheme — a path or query can be case-sensitive.
 */
export function openableUrl(url: string | null | undefined): string | null {
	const trimmed = url?.trim() ?? ''
	const scheme = /^https:\/\//i.exec(trimmed)
	if (scheme === null) return null
	return `https://${trimmed.slice(scheme[0].length)}`
}
